import React, { useImperativeHandle, useRef } from 'react';
import { Card, SectionHeader } from '@/components/Card';
import { MessageSquareText } from 'lucide-react';
import { RichBodyEditor, RichBodyEditorHandle, VariableInsertButton } from './RichBodyEditor';

export interface BodyEditorHandle {
  focus: () => void;
  insertParameter: () => string;
}

interface Props {
  value: string;
  onChange: (next: string) => void;
  /** Returns names used elsewhere (e.g. in header text) so insertNextParameter doesn't collide. */
  externalUsedNames?: () => string[];
}

export const BodyEditor = React.forwardRef<BodyEditorHandle, Props>(({ value, onChange, externalUsedNames }, ref) => {
  const richRef = useRef<RichBodyEditorHandle>(null);
  useImperativeHandle(ref, () => ({
    focus: () => richRef.current?.focus(),
    insertParameter: () => richRef.current?.insertNextParameter() ?? '',
  }));

  return (
    <Card padded={false} className="overflow-hidden">
      <div className="flex items-center justify-between border-b border-border px-5 py-4">
        <SectionHeader
          title="Message body"
          subtitle="The main content. Add parameters to personalize per recipient."
          icon={<MessageSquareText size={15} />}
          className="mb-0"
        />
        <VariableInsertButton onClick={() => richRef.current?.insertNextParameter()} />
      </div>
      <div className="px-5 py-4">
        <RichBodyEditor
          ref={richRef}
          value={value}
          onChange={onChange}
          externalUsedNames={externalUsedNames}
          placeholder="Hi {{firstName}}, your order is on its way."
          maxLength={1024}
          rows={6}
        />
        <div className="mt-2 flex items-center justify-between text-2xs text-text-muted">
          <span>Tip: parameters are non-editable chips. Press <kbd className="rounded bg-surface-alt px-1 py-0.5 font-mono text-2xs">Backspace</kbd> next to one to remove it cleanly.</span>
          <span className="font-mono">{value.length} / 1024</span>
        </div>
      </div>
    </Card>
  );
});
BodyEditor.displayName = 'BodyEditor';
